import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { SECOND_IN_MS } from '@/lib/constants/time';
import { getIntegrationStatus } from '@/modules/common/api/integration-status';
import { EntityType } from '@/modules/common/types/entity-type';
import { IntegrationStatus } from '@/modules/common/types/integration-status';
import { IntegrationServices } from '@/modules/common/types/integrations';

const INTEGRATION_STATUS_QUERY_KEY = ['integrationStatus'];
const INTEGRATION_STATUS_STALE_TIME = 30 * SECOND_IN_MS;

/**
 * Hook that returns the integrations available for a given entity type in reconciliation flows.
 *
 * The integration status endpoint may report a provider as SYNCING or ERROR while it is still
 * connected and should still render as a reconciliation column. For this reason the hook treats
 * any provider returned by the backend for the requested entity type as connected enough to show.
 *
 * @param entityType - The entity type to filter integrations for
 */
export const useConnectedEntityIntegrations = (entityType: EntityType) => {
  const { data: statusData, isLoading } = useQuery({
    queryKey: INTEGRATION_STATUS_QUERY_KEY,
    queryFn: getIntegrationStatus,
    staleTime: INTEGRATION_STATUS_STALE_TIME,
  });

  const integrations = useMemo((): IntegrationServices[] => {
    if (!statusData) return [];

    return statusData.integrations
      .filter(
        descriptor =>
          descriptor.status === IntegrationStatus.Connected &&
          descriptor.entities.some(entity => entity.entityType === entityType)
      )
      .map(descriptor => descriptor.integration);
  }, [statusData, entityType]);

  return { integrations, isLoading };
};
